/**
 * Plans — porównanie 3 planów MASKIL + upgrade UI (Etap 3, 2026-05-14).
 *
 * Pokazuje:
 *   - 3 karty: STARTER / PRO ★ / ENTERPRISE
 *   - Pełna feature matrix (rozwijana)
 *   - Aktualny plan tenanta + zużycie limitów
 *   - Grandfather period info (do 13.06.2026 wszyscy mają PRO)
 *   - Button "Upgrade" (na razie placeholder dla Stripe — wywołuje admin endpoint)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import {
  Check, X, Sparkles, Crown, Shield, Loader2, Gift, ArrowRight,
  TrendingUp, Mail, Users, Database, AlertCircle, ChevronDown, ChevronUp,
} from 'lucide-react';
import { toast } from 'sonner';
import { getPlans, getMyPlan, api } from '../lib/api';
import { invalidateMyPlanCache } from '../components/common/PlanLock';


const PLAN_ICONS = {
  starter:    Shield,
  pro:        Sparkles,
  enterprise: Crown,
};


function fmtPLN(v) {
  if (!v) return '—';
  return new Intl.NumberFormat('pl-PL').format(v) + ' PLN';
}


function PlanCard({ plan, isCurrent, isRecommended, onUpgrade, processing, lang = 'pl' }) {
  const Icon = PLAN_ICONS[plan.id] || Sparkles;
  const monthlySaving = plan.price_pln_monthly * 12 - plan.price_pln_annual;

  return (
    <Card className={`relative ${isRecommended ? 'border-primary ring-2 ring-primary/30' : 'border-border'}`}>
      {isRecommended && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wide">
          {lang === 'pl' ? '⭐ Polecane' : '⭐ Recommended'}
        </div>
      )}
      <CardHeader className="text-center pb-2">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mx-auto mb-2">
          <Icon className="w-6 h-6 text-primary" />
        </div>
        <CardTitle className="text-xl">{plan.name}</CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          {lang === 'pl' ? plan.tagline_pl : plan.tagline_en}
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Price */}
        <div className="text-center pb-3 border-b border-border">
          <div className="text-3xl font-bold">
            {plan.price_label_pl
              ? plan.price_label_pl.split(' ')[0]
              : fmtPLN(plan.price_pln_monthly)}
            <span className="text-xs text-muted-foreground ml-1 font-normal">
              / {lang === 'pl' ? 'mc' : 'mo'}
            </span>
          </div>
          {plan.price_label_pl ? (
            <p className="text-xs text-muted-foreground mt-1">
              {lang === 'pl' ? plan.price_label_pl : plan.price_label_en}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground mt-1">
              {lang === 'pl' ? 'lub' : 'or'} <strong>{fmtPLN(plan.price_pln_annual)}</strong>/
              {lang === 'pl' ? 'rok' : 'yr'} ({lang === 'pl' ? 'oszczędzasz' : 'save'} {fmtPLN(monthlySaving)})
            </p>
          )}
        </div>

        {/* Description */}
        {plan.description_pl && (
          <p className="text-xs text-muted-foreground">
            {lang === 'pl' ? plan.description_pl : plan.description_pl}
          </p>
        )}

        {/* Limits highlights */}
        <div className="space-y-1.5 text-xs">
          <LimitRow
            icon={Users}
            label={lang === 'pl' ? 'Użytkownicy' : 'Users'}
            value={plan.limits.max_users === -1 ? '∞' : plan.limits.max_users}
          />
          <LimitRow
            icon={Sparkles}
            label={lang === 'pl' ? 'Analizy AI / mc' : 'AI analyses / mo'}
            value={plan.limits.max_analyses_per_month === -1 ? '∞' : plan.limits.max_analyses_per_month}
          />
          <LimitRow
            icon={Mail}
            label={lang === 'pl' ? 'RFQ / mc' : 'RFQs / mo'}
            value={plan.limits.max_rfq_per_month === -1 ? '∞' : plan.limits.max_rfq_per_month}
          />
          <LimitRow
            icon={Database}
            label="Storage"
            value={plan.limits.max_storage_gb === -1 ? '∞' : `${plan.limits.max_storage_gb} GB`}
          />
        </div>

        {/* Key features highlight */}
        <div className="space-y-1 pt-2 border-t border-border">
          {[
            { key: 'tenders_list', label: lang === 'pl' ? 'Przetargi (Pipeline A)' : 'Tenders (Pipeline A)' },
            { key: 'investments_module', label: lang === 'pl' ? '💡 Inwestycje (Pipeline B)' : '💡 Investments (Pipeline B)' },
            { key: 'tenders_swz_changes', label: lang === 'pl' ? 'D3 Zmiany SWZ' : 'D3 SWZ Changes' },
            { key: 'suppliers_auto_discovery', label: lang === 'pl' ? 'Auto-discovery dostawców' : 'Auto-discovery suppliers' },
            { key: 'daily_digest', label: lang === 'pl' ? 'Daily Digest 07:00' : 'Daily Digest 07:00' },
            { key: 'multi_user_team', label: lang === 'pl' ? 'Team 5-25 osób' : 'Team 5-25 users' },
            { key: 'white_label', label: lang === 'pl' ? 'White-label' : 'White-label' },
          ].map(({ key, label }) => {
            const has = plan.features[key];
            return (
              <div key={key} className="flex items-center gap-2 text-xs">
                {has ? (
                  <Check className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                ) : (
                  <X className="w-3.5 h-3.5 text-muted-foreground/40 flex-shrink-0" />
                )}
                <span className={has ? '' : 'text-muted-foreground/60 line-through'}>
                  {label}
                </span>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="pt-3">
          {isCurrent ? (
            <Button variant="outline" disabled className="w-full">
              <Check className="w-4 h-4 mr-1" />
              {lang === 'pl' ? 'Twój plan' : 'Your plan'}
            </Button>
          ) : (
            <Button
              onClick={() => onUpgrade(plan.id)}
              disabled={processing}
              className={`w-full gap-1 ${isRecommended ? 'bg-primary hover:bg-primary/90' : ''}`}
            >
              {processing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />}
              {lang === 'pl' ? 'Przejdź na ' : 'Switch to '}{plan.name}
            </Button>
          )}
          <p className="text-[10px] text-muted-foreground text-center mt-2">
            {plan.support.dedicated_success_manager
              ? (lang === 'pl' ? '✓ Dedykowany success manager' : '✓ Dedicated success manager')
              : (lang === 'pl' ? `Wsparcie: ${plan.support.channel}` : `Support: ${plan.support.channel}`)}
            {plan.support.sla_hours && ` · SLA ${plan.support.sla_hours}h`}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}


function LimitRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground flex items-center gap-1.5">
        <Icon className="w-3 h-3" />
        {label}
      </span>
      <span className="font-medium">{value}</span>
    </div>
  );
}


function FullComparisonTable({ comparison, plans, currentPlanId, lang }) {
  const [expanded, setExpanded] = useState(false);
  if (!comparison) return null;

  const keys = expanded ? comparison.feature_keys : comparison.feature_keys.slice(0, 15);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {lang === 'pl' ? 'Pełne porównanie funkcji' : 'Full feature comparison'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left pb-2 font-medium">{lang === 'pl' ? 'Funkcja' : 'Feature'}</th>
                {plans.map((p) => (
                  <th key={p.id} className="text-center pb-2 font-medium min-w-[100px]">
                    {p.name}
                    {p.id === currentPlanId && (
                      <Badge variant="outline" className="ml-1 text-[9px]">aktualny</Badge>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {keys.map((fkey) => {
                const label = comparison.feature_labels_pl[fkey] || fkey;
                return (
                  <tr key={fkey} className="border-b border-border/30">
                    <td className="py-1.5 pr-3 text-muted-foreground">{label}</td>
                    {plans.map((p) => {
                      const has = comparison.matrix[fkey]?.[p.id];
                      return (
                        <td key={p.id} className="text-center py-1.5">
                          {has ? (
                            <Check className="w-3.5 h-3.5 text-emerald-400 inline" />
                          ) : (
                            <X className="w-3.5 h-3.5 text-muted-foreground/30 inline" />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {comparison.feature_keys.length > 15 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setExpanded(!expanded)}
            className="w-full gap-1 mt-2"
          >
            {expanded
              ? <><ChevronUp className="w-3.5 h-3.5" /> {lang === 'pl' ? 'Zwiń' : 'Collapse'}</>
              : <><ChevronDown className="w-3.5 h-3.5" /> {lang === 'pl' ? `Pokaż wszystkie (${comparison.feature_keys.length})` : `Show all (${comparison.feature_keys.length})`}</>
            }
          </Button>
        )}
      </CardContent>
    </Card>
  );
}


function CurrentUsage({ myPlan, lang }) {
  if (!myPlan?.limit_status) return null;

  const usageRows = [
    { key: 'max_users', label: lang === 'pl' ? 'Użytkownicy' : 'Users' },
    { key: 'max_tracked_tenders', label: lang === 'pl' ? 'Obserwowane przetargi' : 'Tracked tenders' },
    { key: 'max_tracked_investments', label: lang === 'pl' ? 'Obserwowane inwestycje' : 'Tracked investments' },
    { key: 'max_rfq_per_month', label: lang === 'pl' ? 'RFQ (ten miesiąc)' : 'RFQ this month' },
    { key: 'max_analyses_per_month', label: lang === 'pl' ? 'Analizy AI (ten miesiąc)' : 'AI analyses this month' },
    { key: 'max_inbox_mailboxes', label: lang === 'pl' ? 'Skrzynki pocztowe' : 'Email mailboxes' },
  ];

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">
          {lang === 'pl' ? 'Twoje zużycie' : 'Your usage'}
          <Badge variant="outline" className="ml-2">
            {myPlan.current_plan_id?.toUpperCase()}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {usageRows.map(({ key, label }) => {
          const data = myPlan.limit_status[key];
          if (!data) return null;
          const limitDisplay = data.limit === -1 ? '∞' : data.limit;
          return (
            <div key={key}>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-muted-foreground">{label}</span>
                <span className={data.near_limit ? 'text-amber-400 font-medium' : ''}>
                  {data.used} / {limitDisplay}
                </span>
              </div>
              {data.limit !== -1 && (
                <Progress
                  value={data.pct}
                  className={`h-1.5 ${
                    data.pct >= 90 ? '[&>div]:bg-red-500' :
                    data.pct >= 70 ? '[&>div]:bg-amber-500' : ''
                  }`}
                />
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}


export default function Plans({ lang = 'pl', tenantId }) {
  const navigate = useNavigate();
  const [plansData, setPlansData] = useState(null);
  const [myPlan, setMyPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([getPlans(), getMyPlan()])
      .then(([plans, mine]) => {
        if (cancelled) return;
        setPlansData(plans);
        setMyPlan(mine);
      })
      .catch((err) => console.error(err))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  async function handleUpgrade(planId) {
    if (!window.confirm(lang === 'pl'
      ? `Zmienić plan na ${planId.toUpperCase()}? (W finalnej wersji Stripe checkout, na razie admin endpoint do testów)`
      : `Switch plan to ${planId.toUpperCase()}? (Final version: Stripe checkout, currently: admin test endpoint)`)) {
      return;
    }
    setProcessing(true);
    try {
      await api.post('/plans/upgrade', { plan_id: planId });
      invalidateMyPlanCache();
      toast.success(lang === 'pl' ? `Plan zmieniony na ${planId.toUpperCase()}` : `Plan changed to ${planId.toUpperCase()}`);
      // Reload
      const [plans, mine] = await Promise.all([getPlans(), getMyPlan()]);
      setPlansData(plans);
      setMyPlan(mine);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Błąd upgrade');
    } finally {
      setProcessing(false);
    }
  }

  if (loading) {
    return (
      <div className="p-6 max-w-6xl mx-auto space-y-4">
        <Skeleton className="h-12 w-1/2" />
        <div className="grid md:grid-cols-3 gap-4">
          <Skeleton className="h-96" /><Skeleton className="h-96" /><Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  if (!plansData) return null;
  const currentPlanId = myPlan?.current_plan_id || 'starter';

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4">
      {/* Header */}
      <div className="text-center py-4">
        <h1 className="text-3xl font-bold mb-2">
          {lang === 'pl' ? 'Plany MASKIL' : 'MASKIL Plans'}
        </h1>
        <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
          {lang === 'pl'
            ? 'Wybierz plan dopasowany do skali Twojej działalności. Wszystkie plany obejmują podstawowe Pipeline A (Przetargi). PRO + Enterprise dodają Pipeline B (Inwestycje) i zaawansowane funkcje AI.'
            : 'Pick a plan that fits your scale. All plans include Pipeline A (Tenders). PRO and Enterprise add Pipeline B (Investments) and advanced AI features.'}
        </p>
      </div>

      {/* Grandfather period notice */}
      {myPlan?.grandfather_active && (
        <Card className="border-emerald-500/30 bg-emerald-500/5">
          <CardContent className="pt-4 pb-4 flex items-start gap-3">
            <Gift className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-medium text-emerald-200">
                {lang === 'pl' ? '🎁 Okres przejściowy aktywny' : '🎁 Grandfather period active'}
              </div>
              <p className="text-xs text-emerald-200/80 mt-0.5">
                {lang === 'pl'
                  ? 'Do 13.06.2026 wszystkie konta mają dostęp do funkcji PRO. Po tej dacie wymagany upgrade jeśli używasz Pipeline B / D3 / Daily Digest.'
                  : 'Until 13.06.2026 all accounts have access to PRO features. After that, upgrade required if using Pipeline B / D3 / Daily Digest.'}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Plan cards */}
      <div className="grid md:grid-cols-3 gap-4 pt-2">
        {plansData.plans.map((plan) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            isCurrent={plan.id === currentPlanId}
            isRecommended={plan.is_recommended}
            onUpgrade={handleUpgrade}
            processing={processing}
            lang={lang}
          />
        ))}
      </div>

      {/* Current usage */}
      <CurrentUsage myPlan={myPlan} lang={lang} />

      {/* Full comparison table */}
      <FullComparisonTable
        comparison={plansData.comparison}
        plans={plansData.plans}
        currentPlanId={currentPlanId}
        lang={lang}
      />

      {/* Footer */}
      <div className="text-center text-xs text-muted-foreground py-4">
        {lang === 'pl'
          ? 'Płatność rocznie = 2 miesiące za darmo (~16% rabat). Zmiana planu w dowolnym momencie, prorata.'
          : 'Annual billing = 2 months free (~16% discount). Change plan anytime, pro-rated.'}
      </div>
    </div>
  );
}
